import React, { useState, useEffect, useContext, useMemo } from "react";
import { Link, useParams, useLocation } from "react-router-dom";
import moment from "moment";
import Swal from "sweetalert2";
import { ColDef } from "ag-grid-community";
import { Plus, Search, Trash2 } from "lucide-react";

import henceforthApi from "../utils/henceforthApi";
import { GlobalContext } from "../context/Provider";
import PaginationLayout from "../components/PaginationLayout";
import henceofrthEnums from "../utils/henceofrthEnums";

import AgGridTable from "../components/ui/AgGridTable";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

interface coupon {
  _id: string;
  name: string;
  coupon_type: string;
  created_at: string;
  uses_time: number;
  expire_date: string;
  max_discount: number;
  price: number;
  currency?: any;
}

function Coupons() {
  const location = useLocation();
  const { authState, handlePagination, handleFilter, handleError } =
    useContext(GlobalContext);
  const params: any = useParams();
  const newParam = new URLSearchParams(location.search);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [getCoupon, setGetCoupon] = useState<Array<coupon>>([]);
  const limit = 10;

  if (authState.access_token) {
    henceforthApi.setToken(authState.access_token);
  }

  const query = new URLSearchParams(location.search);

  const CouponList = async () => {
    try {
      let q = query.toString();
      setLoading(true);
      let apiRes = await henceforthApi.Coupons.getCouponsList(
        q,
        limit,
        Number(params.page) - 1
      );
      setGetCoupon(apiRes.data.data || []);
      setTotalCount(apiRes.data.total_count || 0);
      setLoading(false);
    } catch (error: any) {
      setGetCoupon([]);
      setTotalCount(0);
      setLoading(false);
      handleError(error);
    }
  };

  const handleDelete = (id: string) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#006747",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    }).then(async (result: any) => {
      if (result.isConfirmed) {
        try {
          await henceforthApi.Coupons.delCoupons(id);
          Swal.fire({
            title: "Coupon Deleted",
            text: "Coupon deleted successfully",
            icon: "success",
            confirmButtonText: "OK",
            confirmButtonColor: "#006747",
          });
          CouponList();
        } catch (err: any) {
          Swal.fire({
            title: "Delete Failed",
            text: err?.response?.body?.error_description || "Could not delete coupon",
            icon: "error",
            confirmButtonText: "Cancel",
          });
        }
      }
    });
  };

  useEffect(() => {
    CouponList();
  }, [params.page, newParam.get("search"), query.get("type")]);

  const currentType = query.get("type") || "ALL";

  // AG Grid Column Definitions
  const columnDefs = useMemo<ColDef[]>(() => {
    const pageNum = Number(params.page) || 1;
    return [
      {
        headerName: "Sr No.",
        minWidth: 70,
        maxWidth: 80,
        width: 75,
        valueGetter: (p) => {
          return (pageNum - 1) * limit + (p.node?.rowIndex ?? 0) + 1;
        },
        cellClass: "font-medium text-slate-500",
      },
      {
        headerName: "Coupon Name",
        field: "name",
        minWidth: 160,
        flex: 1,
        cellRenderer: (p: any) => {
          return (
            <span className="font-semibold text-slate-800 text-xs">
              {p.value || "Not Available"}
            </span>
          );
        },
      },
      {
        headerName: "Discount Type",
        field: "coupon_type",
        minWidth: 130,
        width: 140,
        cellRenderer: (p: any) => {
          const type = p.value;
          return (
            <Badge variant={type === "FIXED" ? "success" : "info"}>
              {type ? type.replace(/_/g, " ") : "N/A"}
            </Badge>
          );
        },
      },
      {
        headerName: "Created Date",
        field: "created_at",
        minWidth: 120,
        width: 130,
        valueFormatter: (p) => {
          return p.value ? moment(Number(p.value)).format("DD MMM YYYY") : "N/A";
        },
      },
      {
        headerName: "Expire Date",
        field: "expire_date",
        minWidth: 120,
        width: 130,
        valueFormatter: (p) => {
          return p.value ? moment(Number(p.value)).format("DD MMM YYYY") : "N/A";
        },
      },
      {
        headerName: "Discount Amount",
        minWidth: 130,
        width: 140,
        cellRenderer: (p: any) => {
          const res = p.data;
          if (res?.coupon_type === "FIXED") {
            const sym =
              res?.currency === henceofrthEnums.CurrencyType.NGN
                ? "₦"
                : res?.currency === henceofrthEnums.CurrencyType.INR
                ? "₹"
                : "$";
            return (
              <span className="font-bold text-slate-900 text-sm">
                {`${sym} ${res?.price || 0}`}
              </span>
            );
          }
          return (
            <span className="font-bold text-slate-900 text-sm">
              {res?.max_discount ? `${res.max_discount}%` : "N/A"}
            </span>
          );
        },
      },
      {
        headerName: "Use Times",
        field: "uses_time",
        minWidth: 90,
        width: 100,
        cellRenderer: (p: any) => {
          return (
            <span className="text-slate-700 text-sm font-medium">
              {p.value ?? "0"}
            </span>
          );
        },
      },
      {
        headerName: "Actions",
        minWidth: 70,
        maxWidth: 85,
        width: 75,
        sortable: false,
        filter: false,
        cellRenderer: (p: any) => {
          return (
            <div className="flex items-center justify-center w-full">
              <button
                type="button"
                onClick={() => handleDelete(p.data?._id)}
                title="Delete Coupon"
                className="table-action-dots text-rose-500 hover:text-rose-700"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          );
        },
      },
    ];
  }, [params.page]);

  return (
    <>
      {/* Compact Single Header Toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        {/* Left: Search Input */}
        <div className="relative w-56 sm:w-64">
          <Search className="w-4 h-4 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
          <Input
            name="search"
            defaultValue={newParam.get("search") || ""}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              handleFilter(e.target.name, e.target.value);
            }}
            placeholder="Search coupons..."
            className="search-icon-input h-9 text-sm bg-white border-slate-200 focus:bg-white shadow-2xs"
          />
        </div>

        <div className="flex flex-wrap items-center gap-2.5">
          {/* Type Filter */}
          <div className="w-40">
            <Select
              value={currentType}
              onValueChange={(val) =>
                handleFilter("type", val === "ALL" ? "" : val)
              }
            >
              <SelectTrigger className="h-9 text-sm bg-white shadow-2xs">
                <SelectValue placeholder="All Types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">All Types</SelectItem>
                <SelectItem value="FIXED">Fixed</SelectItem>
                <SelectItem value="PERCENTAGE">Percentage</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Add New Button */}
          <Link to="/coupons/add">
            <Button
              variant="default"
              size="sm"
              className="h-9 px-4 text-sm gap-1.5 shadow-xs"
            >
              <Plus className="w-4 h-4" />
              <span>Add New</span>
            </Button>
          </Link>
        </div>
      </div>

      {/* AG Grid Table */}
      <AgGridTable
        rowData={getCoupon}
        columnDefs={columnDefs}
        loading={loading}
        domLayout="autoHeight"
        emptyMessage="No coupons found"
      />

      <div className="mt-3">
        <PaginationLayout
          count={totalCount}
          data={getCoupon}
          page={Number(params.page)}
          limit={Number(limit)}
          loading={loading}
          onPageChange={(val) => handlePagination(val)}
        />
      </div>
    </>
  );
}

export default Coupons;
